import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Plus, Trash2, Search, Save, Send, Calculator,
  Building2, Package, FileText, Calendar, Percent
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import api, { vendorsAPI, productsAPI, warehousesAPI } from '@/services/api';
import HighlightText from '@/components/HighlightText';

const currencies = [
  { code: 'INR', name: 'Indian Rupee', symbol: '₹' },
  { code: 'USD', name: 'US Dollar', symbol: '$' },
  { code: 'EUR', name: 'Euro', symbol: '€' },
  { code: 'GBP', name: 'British Pound', symbol: '£' },
  { code: 'AED', name: 'UAE Dirham', symbol: 'د.إ' }
];

const CreatePurchaseOrderPage = () => {
  const navigate = useNavigate();
  const { id: editId } = useParams();
  const { toast } = useToast();
  const isEdit = !!editId;

  // Form state
  const [vendor, setVendor] = useState(null);
  const [warehouse, setWarehouse] = useState(null);
  const [currency, setCurrency] = useState('INR');
  const [conversionRate, setConversionRate] = useState(1.0);
  const [items, setItems] = useState([]);
  const [notes, setNotes] = useState('');
  const [expectedDeliveryDate, setExpectedDeliveryDate] = useState('');
  
  // Footer adjustments
  const [billDiscountPercent, setBillDiscountPercent] = useState(0);
  const [billDiscountAmount, setBillDiscountAmount] = useState(0);
  const [otherChargesAdd, setOtherChargesAdd] = useState(0);
  const [otherChargesDeduct, setOtherChargesDeduct] = useState(0);
  const [roundOff, setRoundOff] = useState(0);

  // Lookup data
  const [vendors, setVendors] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [products, setProducts] = useState([]);
  
  // UI state
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [vendorDialogOpen, setVendorDialogOpen] = useState(false);
  const [vendorSearch, setVendorSearch] = useState('');
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [productSearch, setProductSearch] = useState('');
  const searchReqRef = useRef(0);
  const [editingItemIndex, setEditingItemIndex] = useState(null);

  // Fetch initial data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [vendorsRes, warehousesRes] = await Promise.all([
          vendorsAPI.list(),
          warehousesAPI.list()
        ]);
        setVendors(vendorsRes.data || []);
        setWarehouses(warehousesRes.data?.items || warehousesRes.data || []);
      } catch (error) {
        toast({ title: 'Error', description: 'Failed to load data', variant: 'destructive' });
      }
    };
    fetchData();
  }, [toast]);

  // Load PO for editing
  useEffect(() => {
    if (editId) {
      const loadPO = async () => {
        setLoading(true);
        try {
          const res = await api.get(`/po-v2/${editId}`);
          const po = res.data;
          
          if (po.status !== 'draft') {
            toast({ title: 'Error', description: 'Can only edit POs in DRAFT status', variant: 'destructive' });
            navigate('/hq/purchase/orders');
            return;
          }
          
          // Set vendor from snapshot
          if (po.vendor_snapshot) {
            setVendor({
              id: po.vendor_id,
              ...po.vendor_snapshot
            });
          }
          
          // Set warehouse
          if (po.warehouse_id) {
            const wh = warehouses.find(w => w.id === po.warehouse_id);
            setWarehouse(wh || { id: po.warehouse_id, name: po.warehouse_name });
          }
          
          setCurrency(po.currency || 'INR');
          setConversionRate(po.conversion_rate || 1.0);
          setItems(po.items || []);
          setNotes(po.notes || '');
          setExpectedDeliveryDate(po.expected_delivery_date || '');
          setBillDiscountPercent(po.bill_discount_percent || 0);
          setBillDiscountAmount(po.bill_discount_amount || 0);
          setOtherChargesAdd(po.other_charges_add || 0);
          setOtherChargesDeduct(po.other_charges_deduct || 0);
          setRoundOff(po.round_off || 0);
        } catch (error) {
          toast({ title: 'Error', description: 'Failed to load PO', variant: 'destructive' });
        } finally {
          setLoading(false);
        }
      };
      loadPO();
    }
  }, [editId, warehouses, navigate, toast]);

  // Search products (debounced + out-of-order responses ignored)
  const searchProducts = useCallback(async (query) => {
    if (!query || query.trim().length < 2) {
      setProducts([]);
      return;
    }
    const reqId = ++searchReqRef.current;
    try {
      const res = await productsAPI.search(query.trim());
      if (reqId !== searchReqRef.current) return;
      setProducts(res.data?.items || res.data || []);
    } catch (error) {
      console.error('Product search error:', error);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => searchProducts(productSearch), 300);
    return () => clearTimeout(timer);
  }, [productSearch, searchProducts]);

  // Calculate totals
  const calculateTotals = useCallback(() => {
    let subtotal = 0;
    let gstTotal = 0;
    
    items.forEach(item => {
      const unitPriceInr = (item.unit_price || 0) * (item.conversion_rate || conversionRate);
      const lineSubtotal = (item.qty || 0) * unitPriceInr;
      const lineDiscount = lineSubtotal * ((item.discount_percent || 0) / 100) + (item.discount_amount || 0);
      const taxable = lineSubtotal - lineDiscount;
      const lineGst = taxable * ((item.gst_percent || 0) / 100);
      
      subtotal += taxable;
      gstTotal += lineGst;
    });
    
    const billDisc = subtotal * (billDiscountPercent / 100) + billDiscountAmount;
    // Safely parse roundOff - handle string values during typing
    const roundOffValue = typeof roundOff === 'string' ? (parseFloat(roundOff) || 0) : (roundOff || 0);
    const grandTotal = subtotal - billDisc + gstTotal + otherChargesAdd - otherChargesDeduct + roundOffValue;
    
    return {
      subtotal: subtotal.toFixed(2),
      gstTotal: gstTotal.toFixed(2),
      billDiscount: billDisc.toFixed(2),
      grandTotal: grandTotal.toFixed(2)
    };
  }, [items, conversionRate, billDiscountPercent, billDiscountAmount, otherChargesAdd, otherChargesDeduct, roundOff]);

  const totals = calculateTotals();

  // Add product to items with previous purchase history fetch
  const addProduct = async (product) => {
    // Get prices from product master - check current_price object first, then direct fields
    const currentPrice = product.current_price || {};
    
    // Extract cost price (check multiple possible locations)
    let costPrice = currentPrice.cost_price || currentPrice.basic_cost || 
                    product.cost_price || product.last_purchase_price || 0;
    
    // Extract MRP
    let mrp = currentPrice.mrp || product.mrp || 0;
    
    // Extract GST/Tax percent - PRIORITY: tax_rate first (from product_prices collection)
    let gstPercent = currentPrice.tax_rate ?? currentPrice.gst_percent ?? currentPrice.tax_percent ?? 
                     product.tax_rate ?? product.gst_percent ?? product.tax_percent ?? 0;
    
    // Fetch last purchase history from vendor if vendor is selected
    if (vendor) {
      try {
        const vendorId = vendor.id || vendor.vendor_id;
        const res = await api.get(`/products/${product.id}/vendor-purchase-history?vendor_id=${vendorId}`);
        if (res.data?.found) {
          // Use values from last purchase (override product master values)
          costPrice = res.data.last_rate || costPrice;
          mrp = res.data.mrp || mrp;
          gstPercent = res.data.gst_percent > 0 ? res.data.gst_percent : gstPercent;
          
          toast({
            title: 'Previous Purchase Found',
            description: `Last Rate: ₹${res.data.last_rate}, MRP: ₹${res.data.mrp}, GST: ${res.data.gst_percent}%`,
          });
        }
      } catch (err) {
        console.log('No previous purchase history found');
      }
    }
    
    const newItem = {
      product_id: product.id,
      product_name: product.name,
      sku: product.sku,
      hsn_code: product.hsn_code || '',
      uom: product.purchase_uom || product.base_uom || 'PCS',
      qty: 1,
      unit_price: costPrice,
      mrp: mrp,
      currency: currency,
      conversion_rate: conversionRate,
      weight_per_unit: product.weight || 0,
      cbm_per_unit: product.cbm || 0,
      gst_percent: gstPercent,
      discount_percent: 0,
      discount_amount: 0
    };
    
    setItems([...items, newItem]);
    setProductDialogOpen(false);
    setProductSearch('');
  };

  // Handle Enter key navigation - move to next field in same row
  const handleKeyDown = (e, currentIndex, currentField) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      
      // Define field order for Enter key navigation within a row
      const fieldOrder = ['qty', 'price', 'gst', 'discount'];
      
      const currentFieldIndex = fieldOrder.indexOf(currentField);
      
      if (currentFieldIndex !== -1 && currentFieldIndex < fieldOrder.length - 1) {
        // Move to next field in same row
        const nextField = fieldOrder[currentFieldIndex + 1];
        const nextInput = document.querySelector(`[data-testid="po-line-${nextField}-${currentIndex}"]`);
        if (nextInput) {
          nextInput.focus();
          if (nextInput.select) nextInput.select();
        }
      } else {
        // Last field in row - move to first field of next row if exists
        const nextRowFirstInput = document.querySelector(`[data-testid="po-line-qty-${currentIndex + 1}"]`);
        if (nextRowFirstInput) {
          nextRowFirstInput.focus();
          if (nextRowFirstInput.select) nextRowFirstInput.select();
        }
      }
    }
  };

  // Update item
  const updateItem = (index, field, value) => {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], [field]: value };
    setItems(newItems);
  };

  // Remove item
  const removeItem = (index) => {
    setItems(items.filter((_, i) => i !== index));
  };

  // Save PO
  const savePO = async (submitForApproval = false) => {
    if (!vendor) {
      toast({ title: 'Error', description: 'Please select a vendor', variant: 'destructive' });
      return;
    }
    if (!warehouse) {
      toast({ title: 'Error', description: 'Please select a warehouse', variant: 'destructive' });
      return;
    }
    if (items.length === 0) {
      toast({ title: 'Error', description: 'Please add at least one item', variant: 'destructive' });
      return;
    }
    
    setSaving(true);
    try {
      const payload = {
        vendor_id: vendor.id || vendor.vendor_id,
        warehouse_id: warehouse.id,
        currency,
        conversion_rate: conversionRate,
        items: items.map(item => ({
          product_id: item.product_id,
          qty: parseInt(item.qty) || 0,
          unit_price: parseFloat(item.unit_price) || 0,
          hsn_code: item.hsn_code,
          uom: item.uom,
          currency: item.currency || currency,
          conversion_rate: item.conversion_rate || conversionRate,
          weight_per_unit: parseFloat(item.weight_per_unit) || 0,
          cbm_per_unit: parseFloat(item.cbm_per_unit) || 0,
          gst_percent: parseFloat(item.gst_percent) || 0,
          discount_percent: parseFloat(item.discount_percent) || 0,
          discount_amount: parseFloat(item.discount_amount) || 0
        })),
        bill_discount_percent: parseFloat(billDiscountPercent) || 0,
        bill_discount_amount: parseFloat(billDiscountAmount) || 0,
        other_charges_add: parseFloat(otherChargesAdd) || 0,
        other_charges_deduct: parseFloat(otherChargesDeduct) || 0,
        round_off: parseFloat(roundOff) || 0,
        notes,
        expected_delivery_date: expectedDeliveryDate || null
      };
      
      let res;
      if (isEdit) {
        res = await api.put(`/po-v2/${editId}`, payload);
      } else {
        res = await api.post('/po-v2', payload);
      }
      
      const poId = res.data?.id || editId;
      
      if (submitForApproval && poId) {
        await api.put(`/po-v2/${poId}/submit-for-approval`);
        toast({ title: 'Success', description: 'PO submitted for approval' });
      } else {
        toast({ title: 'Success', description: isEdit ? 'PO updated' : 'PO created as draft' });
      }
      
      navigate('/hq/purchase/orders');
    } catch (error) {
      toast({ 
        title: 'Error', 
        description: error.response?.data?.detail || 'Failed to save PO', 
        variant: 'destructive' 
      });
    } finally {
      setSaving(false);
    }
  };

  // Round-off input handler (supports negative while typing)
  // Round-off handler - allow free typing of numbers, +, -, and .
  const handleRoundOffChange = (e) => {
    const v = e.target.value;
    // Allow empty, partial inputs like '-', '.', '-.', '+', '+.' while typing
    // Only allow: digits, one decimal point, optional leading +/-
    const isValidPartial = /^[+-]?\.?\d*\.?\d*$/.test(v);
    if (v === '' || isValidPartial) {
      setRoundOff(v);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500"></div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 bg-gray-50 min-h-screen" data-testid="create-po-page">
      <Toaster />

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/hq/purchase/orders')} data-testid="po-back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-800">
              {isEdit ? 'Edit Purchase Order' : 'Create Purchase Order'}
            </h1>
            <p className="text-gray-500">
              {currency !== 'INR' ? 'International PO' : 'Local PO'} - Fill in the details below
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={() => savePO(false)} 
            disabled={saving}
            data-testid="po-save-draft-btn"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Saving...' : 'Save Draft'}
          </Button>
          <Button 
            onClick={() => savePO(true)} 
            disabled={saving}
            className="bg-teal-500 hover:bg-teal-600"
            data-testid="po-submit-btn"
          >
            <Send className="w-4 h-4 mr-2" />
            Submit for Approval
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content - Left 2 columns */}
        <div className="lg:col-span-2 space-y-6">
          {/* Vendor & Warehouse Selection */}
          <Card className="bg-white">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg flex items-center gap-2">
                <Building2 className="w-5 h-5 text-teal-500" />
                Vendor & Warehouse
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Vendor Selector */}
                <div>
                  <Label>Vendor *</Label>
                  <div 
                    className="mt-1 p-3 border rounded-lg cursor-pointer hover:border-teal-500 transition-colors"
                    onClick={() => setVendorDialogOpen(true)}
                    data-testid="po-vendor-selector"
                  >
                    {vendor ? (
                      <div>
                        <p className="font-medium">{vendor.vendor_name || vendor.name}</p>
                        <p className="text-sm text-gray-500">{vendor.vendor_code || vendor.code}</p>
                      </div>
                    ) : (
                      <p className="text-gray-400">Click to select vendor</p>
                    )}
                  </div>
                </div>

                {/* Warehouse Selector */}
                <div>
                  <Label>Destination Warehouse *</Label>
                  <Select 
                    value={warehouse?.id || ''} 
                    onValueChange={(val) => setWarehouse(warehouses.find(w => w.id === val))}
                  >
                    <SelectTrigger className="mt-1" data-testid="po-warehouse-select">
                      <SelectValue placeholder="Select warehouse" />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      {warehouses.map(wh => (
                        <SelectItem key={wh.id} value={wh.id}>{wh.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Currency & Conversion */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                <div>
                  <Label>Currency</Label>
                  <Select value={currency} onValueChange={setCurrency}>
                    <SelectTrigger className="mt-1" data-testid="po-currency-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      {currencies.map(c => (
                        <SelectItem key={c.code} value={c.code}>
                          {c.symbol} {c.code} - {c.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {currency !== 'INR' && (
                  <div>
                    <Label>Conversion Rate (1 {currency} = ₹)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={conversionRate}
                      onChange={(e) => setConversionRate(parseFloat(e.target.value) || 1)}
                      className="mt-1"
                      data-testid="po-conversion-rate"
                    />
                  </div>
                )}
                <div>
                  <Label>Expected Delivery</Label>
                  <Input
                    type="date"
                    value={expectedDeliveryDate}
                    onChange={(e) => setExpectedDeliveryDate(e.target.value)}
                    className="mt-1"
                    data-testid="po-delivery-date"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Items */}
          <Card className="bg-white">
            <CardHeader className="pb-4 flex flex-row items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <Package className="w-5 h-5 text-teal-500" />
                Line Items ({items.length})
              </CardTitle>
              <Button 
                onClick={() => setProductDialogOpen(true)}
                size="sm"
                className="bg-teal-500 hover:bg-teal-600"
                data-testid="po-line-add-btn"
              >
                <Plus className="w-4 h-4 mr-1" /> Add Item
              </Button>
            </CardHeader>
            <CardContent>
              {items.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Package className="w-12 h-12 mx-auto mb-2 opacity-30" />
                  <p>No items added yet. Click &quot;Add Item&quot; to start.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50">
                        <TableHead className="w-[250px]">Product</TableHead>
                        <TableHead className="w-[80px] text-center">Qty</TableHead>
                        <TableHead className="w-[100px] text-right">Unit Price</TableHead>
                        <TableHead className="w-[80px] text-right">MRP</TableHead>
                        <TableHead className="w-[80px] text-center">GST %</TableHead>
                        <TableHead className="w-[80px] text-center">Disc %</TableHead>
                        <TableHead className="w-[100px] text-right">Total</TableHead>
                        <TableHead className="w-[50px]"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {items.map((item, index) => {
                        const unitPriceInr = (item.unit_price || 0) * (item.conversion_rate || conversionRate);
                        const lineSubtotal = (item.qty || 0) * unitPriceInr;
                        const lineDiscount = lineSubtotal * ((item.discount_percent || 0) / 100);
                        const taxable = lineSubtotal - lineDiscount;
                        const lineGst = taxable * ((item.gst_percent || 0) / 100);
                        const lineTotal = taxable + lineGst;
                        
                        return (
                          <TableRow key={index} data-testid={`po-line-row-${index}`}>
                            <TableCell>
                              <p className="font-medium text-sm">{item.product_name}</p>
                              <p className="text-xs text-gray-500">{item.sku} | HSN: {item.hsn_code || '-'}</p>
                            </TableCell>
                            <TableCell>
                              <Input
                                type="number"
                                min="1"
                                value={item.qty}
                                onChange={(e) => updateItem(index, 'qty', parseInt(e.target.value) || 0)}
                                onKeyDown={(e) => handleKeyDown(e, index, 'qty')}
                                className="w-16 text-center"
                                data-testid={`po-line-qty-${index}`}
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                type="number"
                                step="0.01"
                                value={item.unit_price}
                                onChange={(e) => updateItem(index, 'unit_price', parseFloat(e.target.value) || 0)}
                                onKeyDown={(e) => handleKeyDown(e, index, 'price')}
                                className="w-24 text-right"
                                data-testid={`po-line-price-${index}`}
                              />
                            </TableCell>
                            <TableCell className="text-right text-gray-600">
                              ₹{(item.mrp || 0).toFixed(2)}
                            </TableCell>
                            <TableCell>
                              <Input
                                type="number"
                                step="0.01"
                                value={item.gst_percent}
                                onChange={(e) => updateItem(index, 'gst_percent', parseFloat(e.target.value) || 0)}
                                onKeyDown={(e) => handleKeyDown(e, index, 'gst')}
                                className="w-16 text-center"
                                data-testid={`po-line-gst-${index}`}
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                type="number"
                                step="0.01"
                                value={item.discount_percent}
                                onChange={(e) => updateItem(index, 'discount_percent', parseFloat(e.target.value) || 0)}
                                onKeyDown={(e) => handleKeyDown(e, index, 'discount')}
                                className="w-16 text-center"
                                data-testid={`po-line-discount-${index}`}
                              />
                            </TableCell>
                            <TableCell className="text-right font-medium">
                              ₹{lineTotal.toFixed(2)}
                            </TableCell>
                            <TableCell>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => removeItem(index)}
                                className="text-red-500 hover:text-red-700"
                                data-testid={`po-line-remove-${index}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Notes */}
          <Card className="bg-white">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg flex items-center gap-2">
                <FileText className="w-5 h-5 text-teal-500" />
                Notes
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add any notes or special instructions..."
                rows={3}
                data-testid="po-notes"
              />
            </CardContent>
          </Card>
        </div>

        {/* Right Sidebar - Vendor Info & Totals */}
        <div className="space-y-6">
          {/* Vendor Snapshot */}
          {vendor && (
            <Card className="bg-white border-teal-200">
              <CardHeader className="pb-2 bg-teal-50">
                <CardTitle className="text-sm text-teal-700">Vendor Details</CardTitle>
              </CardHeader>
              <CardContent className="pt-4 text-sm">
                <p className="font-semibold text-gray-800">{vendor.vendor_name || vendor.name}</p>
                {vendor.legal_name && <p className="text-gray-600">{vendor.legal_name}</p>}
                <div className="mt-2 text-gray-500">
                  {vendor.address_line1 || vendor.address?.address_line1 || vendor.address?.line1 || vendor.address?.street}
                  {(vendor.city || vendor.address?.city) && <>, {vendor.city || vendor.address?.city}</>}
                  {(vendor.state || vendor.address?.state) && <>, {vendor.state || vendor.address?.state}</>}
                  {(vendor.pincode || vendor.address?.pincode) && <> - {vendor.pincode || vendor.address?.pincode}</>}
                </div>
                {(vendor.gstin || vendor.gstin) && (
                  <p className="mt-2"><span className="text-gray-400">GSTIN:</span> {vendor.gstin}</p>
                )}
                {vendor.is_international && (
                  <Badge className="mt-2 bg-amber-100 text-amber-700">International</Badge>
                )}
              </CardContent>
            </Card>
          )}

          {/* Totals */}
          <Card className="bg-white">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Calculator className="w-5 h-5 text-teal-500" />
                Order Summary
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Subtotal</span>
                <span className="font-medium">₹{totals.subtotal}</span>
              </div>
              
              {/* Bill Discount */}
              <div className="flex items-center gap-2">
                <Label className="text-sm text-gray-500 w-24">Bill Disc %</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={billDiscountPercent}
                  onChange={(e) => setBillDiscountPercent(parseFloat(e.target.value) || 0)}
                  className="w-20 text-right"
                  data-testid="po-bill-discount-percent"
                />
                <span className="text-gray-400">or</span>
                <Input
                  type="number"
                  step="0.01"
                  value={billDiscountAmount}
                  onChange={(e) => setBillDiscountAmount(parseFloat(e.target.value) || 0)}
                  className="w-24 text-right"
                  placeholder="₹ Amt"
                  data-testid="po-bill-discount-amount"
                />
              </div>
              
              <div className="flex justify-between text-sm text-red-600">
                <span>(-) Bill Discount</span>
                <span>-₹{totals.billDiscount}</span>
              </div>
              
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">GST Total</span>
                <span className="font-medium">₹{totals.gstTotal}</span>
              </div>
              
              {/* Other Charges */}
              <div className="flex items-center gap-2">
                <Label className="text-sm text-gray-500 w-24">Other (+)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={otherChargesAdd}
                  onChange={(e) => setOtherChargesAdd(parseFloat(e.target.value) || 0)}
                  className="flex-1 text-right"
                  data-testid="po-other-charges-add"
                />
              </div>
              
              <div className="flex items-center gap-2">
                <Label className="text-sm text-gray-500 w-24">Other (-)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={otherChargesDeduct}
                  onChange={(e) => setOtherChargesDeduct(parseFloat(e.target.value) || 0)}
                  className="flex-1 text-right"
                  data-testid="po-other-charges-deduct"
                />
              </div>
              
              {/* Round Off - MUST allow negative */}
              <div className="flex items-center gap-2">
                <Label className="text-sm text-gray-500 w-24">Round Off</Label>
                <Input
                  type="text"
                  value={roundOff}
                  onChange={handleRoundOffChange}
                  className="flex-1 text-right"
                  placeholder="Can be negative"
                  data-testid="po-round-off"
                />
              </div>
              
              <div className="border-t pt-3 flex justify-between text-lg font-bold">
                <span>Grand Total</span>
                <span className="text-teal-600">₹{totals.grandTotal}</span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Vendor Selection Dialog */}
      <Dialog open={vendorDialogOpen} onOpenChange={setVendorDialogOpen}>
        <DialogContent className="bg-white max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Select Vendor</DialogTitle>
          </DialogHeader>
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search vendors..."
              value={vendorSearch}
              onChange={(e) => setVendorSearch(e.target.value)}
              className="pl-10"
              data-testid="vendor-search-input"
            />
          </div>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {vendors
              .filter(v => 
                !vendorSearch || 
                v.name?.toLowerCase().includes(vendorSearch.toLowerCase()) ||
                v.code?.toLowerCase().includes(vendorSearch.toLowerCase())
              )
              .map(v => (
                <div
                  key={v.id}
                  className="p-3 border rounded-lg cursor-pointer hover:border-teal-500 hover:bg-teal-50"
                  onClick={() => {
                    setVendor({
                      id: v.id,
                      vendor_id: v.id,
                      vendor_code: v.code,
                      vendor_name: v.name,
                      legal_name: v.legal_name,
                      address_line1: v.address?.address_line1 || v.address?.line1 || v.address?.street,
                      city: v.address?.city,
                      state: v.address?.state,
                      country: v.address?.country || 'India',
                      pincode: v.address?.pincode || v.address?.postal_code,
                      gstin: v.gstin,
                      is_international: v.is_international || (v.address?.country?.toLowerCase() !== 'india')
                    });
                    setVendorDialogOpen(false);
                    
                    // Auto-set currency for international vendors
                    if (v.is_international || (v.address?.country?.toLowerCase() !== 'india')) {
                      setCurrency('USD');
                    }
                  }}
                  data-testid={`vendor-option-${v.id}`}
                >
                  <p className="font-medium">{v.name}</p>
                  <p className="text-sm text-gray-500">{v.code} | {v.address?.city}</p>
                  {v.is_international && <Badge className="mt-1 bg-amber-100 text-amber-700 text-xs">International</Badge>}
                </div>
              ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Product Selection Dialog */}
      <Dialog open={productDialogOpen} onOpenChange={setProductDialogOpen}>
        <DialogContent className="bg-white max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Product</DialogTitle>
          </DialogHeader>
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search by name, SKU, HSN, or barcode..."
              value={productSearch}
              onChange={(e) => setProductSearch(e.target.value)}
              className="pl-10"
              autoFocus
              data-testid="product-search-input"
            />
          </div>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {products.length === 0 ? (
              <p className="text-center text-gray-500 py-8">
                {productSearch.length < 2 ? 'Type at least 2 characters to search' : 'No products found'}
              </p>
            ) : (
              products.map(p => (
                <div
                  key={p.id}
                  className="p-3 border rounded-lg cursor-pointer hover:border-teal-500 hover:bg-teal-50"
                  onClick={() => addProduct(p)}
                  data-testid={`product-option-${p.id}`}
                >
                  <p className="font-medium"><HighlightText text={p.name} query={productSearch} /></p>
                  <p className="text-sm text-gray-500"><HighlightText text={p.sku} query={productSearch} /></p>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CreatePurchaseOrderPage;
